/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useState } from "react"
import { shallowEqual, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Button, Row, Col } from "@duxton-web/ui"

import { AttributeCluster } from "types/models/menu"
import { hasFormErrors } from "utils/form_helper"
import { ItemAttributeValue } from "types/models/item_attribute"
import { storageHelper } from "utils/storage"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useIsAllowSaleAlcohol } from "pages/menu/detail/hooks/useIsAllowSaleAlcohol"
import { useIsAllowSaleTobacco } from "pages/menu/detail/hooks/useIsAllowSaleTobacco"
import styles from "./AttributeDrawer.modules.css"
import { AttributeDrawerReview } from "./AttributeDrawerReview"
import {
  BCRS_ELIGIBLE_FIELD,
  BCRS_UNIT_COUNT_FIELD,
} from "../ItemDrawer/BCRSItemSection"
import { AttributeDrawerFreeText } from "./AttributeDrawerFreeText"
import { removeEmptyData, transformFormData } from "./AttributeDrawer.utils"
import { ItemExceedChangeLimit } from "../alerts"
import { AttributeDrawerPreDefined } from "./AttributeDrawerPreDefined"
import { AttributeDrawerDecimal } from "./AttributeDrawerDecimal"
import AlcoholTagging from "./AlcoholTagging"
import TobaccoTagging from "./TobaccoTagging"

export interface AttributeDrawerProps extends FormComponentProps {
  onClose: (e: any) => void
  onSaveButton: (selectedAttributes?: ItemAttributeValue[]) => void
  onBackAttribute: () => void
  itemID?: string
  itemAttributes?: ItemAttributeValue[]
  finalAttributeClusterKey: string[]
  attributeClusters?: { [s: string]: AttributeCluster }
  isLoadingPagination?: boolean
  errorShown?: boolean
  priceRange?: {
    min: string
    max: string
  }
  uploadedImages?: string[]
  showAttributeDrawer: boolean
  specialItemType?: string
}

type AttributeDrawerComponentContent = "form" | "review"
export const AttributeDrawer: FC<AttributeDrawerProps> = ({
  onBackAttribute,
  onSaveButton,
  form,
  itemID,
  itemAttributes,
  finalAttributeClusterKey,
  attributeClusters,
  isLoadingPagination,
  errorShown,
  priceRange,
  uploadedImages,
  showAttributeDrawer,
  specialItemType,
}: AttributeDrawerProps) => {
  const textMap = useTranslationsMapTyped({
    backButton: "itemAttributes.button.back",
    reviewButton: "itemAttributes.button.review",
    saveButton: "itemAttributes.button.save",
  })

  const {
    isMenuGroup,
    isEnableFreeTextItemAttribute,
    isEnableDecimalItemAttribute,
    alcoholPrediction,
    tobaccoPrediction,
  } = useSelector((state: AppState) => state?.menu, shallowEqual)

  const isAllowSaleAlcohol = useIsAllowSaleAlcohol()
  const isAllowSaleTobacco = useIsAllowSaleTobacco()

  // Disable form rule when the attribute drawer is hidden
  const disableFormRule = !showAttributeDrawer

  const [shownContent, setShownContent] = useState<
    AttributeDrawerComponentContent
  >("form")

  const [selectedAttributes, setSelectedAttributes] = useState<
    ItemAttributeValue[] | undefined
  >(itemAttributes)

  const handleChangeAttributes = (value: ItemAttributeValue[]) => {
    setSelectedAttributes(value)
  }

  const pricePrint =
    priceRange && priceRange.min !== priceRange.max
      ? `${storageHelper.getUserCurrency()} ${priceRange.min} - ${
          priceRange.max
        }`
      : `${storageHelper.getUserCurrency()} ${priceRange?.min}`

  const handleFormItemType = (value: AttributeCluster, key: string) => {
    if (
      (!isMenuGroup || isEnableFreeTextItemAttribute) &&
      value.attributeClusterType.toLowerCase() === "freetext"
    ) {
      return (
        <AttributeDrawerFreeText
          key={`${key}-freetext`}
          attributeCluster={value}
          selectedAttributes={selectedAttributes}
          handleChangeAttributes={handleChangeAttributes}
          itemID={itemID}
          form={form}
          disableFormRule={disableFormRule}
        />
      )
    }
    if (
      (!isMenuGroup || isEnableDecimalItemAttribute) &&
      value.attributeClusterType.toLowerCase() === "numerical"
    ) {
      return (
        <AttributeDrawerDecimal
          key={`${key}-numerical`}
          attributeCluster={value}
          selectedAttributes={selectedAttributes}
          handleChangeAttributes={handleChangeAttributes}
          itemID={itemID}
          form={form}
          disableFormRule={disableFormRule}
        />
      )
    }
    if (value.attributeClusterType.toLowerCase() === "predefined") {
      return (
        <AttributeDrawerPreDefined
          key={`${key}-predefined`}
          attributeCluster={value}
          isLoadingPagination={isLoadingPagination}
          selectedAttributes={selectedAttributes}
          initialValueAttributes={itemAttributes}
          handleChangeAttributes={handleChangeAttributes}
          itemID={itemID}
          form={form}
          disableFormRule={disableFormRule}
        />
      )
    }
    return undefined
  }

  const handleAlcoholChange = (value: number) => {
    if (value === 1) {
      form.setFieldsValue({ tobaccoTagging: 0 })
    }
  }

  const handleTobaccoChange = (value: number) => {
    if (value === 1) {
      form.setFieldsValue({ alcoholTagging: 0 })
    }
  }

  const formValues = form.getFieldsValue()

  return (
    <div data-testid="attribute-drawer">
      {errorShown && <ItemExceedChangeLimit itemID={itemID ?? ""} />}
      <div className={styles.attributeDrawer}>
        <div className={shownContent === "form" ? styles.display : styles.hide}>
          {isAllowSaleAlcohol && (
            <AlcoholTagging
              form={form}
              alcoholPrediction={alcoholPrediction}
              showAttributeDrawer={showAttributeDrawer}
              specialItemType={specialItemType}
              onAlcoholChange={handleAlcoholChange}
            />
          )}
          {isAllowSaleTobacco && (
            <TobaccoTagging
              form={form}
              tobaccoPrediction={tobaccoPrediction}
              showAttributeDrawer={showAttributeDrawer}
              specialItemType={specialItemType}
              onTobaccoChange={handleTobaccoChange}
            />
          )}
          {finalAttributeClusterKey?.map(
            (key: any) =>
              !!attributeClusters?.[key] &&
              handleFormItemType(attributeClusters[key], key),
          )}
        </div>
        <div
          className={shownContent === "review" ? styles.display : styles.hide}
        >
          <AttributeDrawerReview
            price={pricePrint}
            menuName={form.getFieldValue("itemName")}
            // Only pass images when in review mode to avoid unnecessary image loading
            // while the review section is hidden via CSS (the component still renders)
            imagesSrcList={
              shownContent === "review" ? uploadedImages ?? [] : []
            }
            dataSource={removeEmptyData([
              ...transformFormData(formValues),
              ...transformFormData(formValues?.attributeForm ?? {}),
            ])}
            isAlcohol={form.getFieldValue("alcoholTagging")}
            isTobacco={form.getFieldValue("tobaccoTagging")}
            isBcrsEligible={form.getFieldValue(BCRS_ELIGIBLE_FIELD)}
            bcrsUnitCount={form.getFieldValue(BCRS_UNIT_COUNT_FIELD)}
          />
        </div>
      </div>
      <div className={styles.drawerFooter}>
        {shownContent === "form" ? (
          <Row gutter={12}>
            <Col span={6}>
              <Button block onClick={onBackAttribute}>
                {textMap.backButton}
              </Button>
            </Col>
            <Col flex="auto">
              <Button
                type="primary"
                data-testid="review-attribute-item"
                block
                disabled={hasFormErrors(form)}
                onClick={() =>
                  form.validateFields(formError => {
                    if (formError) return
                    setShownContent("review")
                  })
                }
              >
                {textMap.reviewButton}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row gutter={12}>
            <Col span={6}>
              <Button
                data-testid="back-attribute-form"
                block
                onClick={() => setShownContent("form")}
              >
                {textMap.backButton}
              </Button>
            </Col>
            <Col flex="auto">
              <Button
                type="primary"
                data-testid="save-attribute-item"
                block
                onClick={() => onSaveButton(selectedAttributes)}
              >
                {textMap.saveButton}
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}
